import EdgeScroll from '../components/EdgeScroll';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Paper, Text, Title, Badge, Group, Box, Stack, Drawer, Button,
  Flex, Loader, ThemeIcon, Table, Pagination, Tooltip, Select, Modal,
} from '@mantine/core';
import {
  Pulse, Brain, Lightning, ShieldCheck, ShieldWarning,
  CurrencyDollar, ChartBar, Warning, ArrowsClockwise, CheckCircle,
  PencilSimple, TextAa, Minus, Plus, Pause, Play, Gear, Funnel, Power,
} from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';
import AnimatedLogo from '../components/AnimatedLogo';
import ExportMenu from '../components/ExportMenu';
import api from '../utils/api';
import { currencySymbol } from '../utils/money';
import StickyHeader from '../components/StickyHeader';

// Labele akcji są tłumaczone w czasie renderu przez t('activity.actions.<action>').
const ACTION_CONFIG = {
  // Optymalizacje
  optimize:                   { icon: Lightning,        color: 'green'  },
  auto_apply:                 { icon: Lightning,        color: 'green'  },
  // Zmiany kampanii
  ad_group_update:            { icon: PencilSimple,     color: 'green'  },
  campaign_settings_update:   { icon: Gear,             color: 'blue'   },
  management_toggle:          { icon: Power,            color: 'gray'   },
  // Słowa kluczowe
  keyword_add:                { icon: Plus,             color: 'green'  },
  keyword_add_batch:          { icon: Plus,             color: 'green'  },
  keyword_pause:              { icon: Pause,            color: 'yellow' },
  keyword_enable:             { icon: Play,             color: 'green'  },
  keyword_remove:             { icon: Minus,            color: 'red'    },
  // Reklamy
  ad_create:                  { icon: TextAa,           color: 'green'  },
  // Analiza
  analyze:                    { icon: Brain,            color: 'brand'  },
  audit:                      { icon: ChartBar,         color: 'brand'  },
  draft_analyzed:             { icon: Brain,            color: 'brand'  },
  // Zdrowie
  health_alert:               { icon: ShieldWarning,    color: 'red'    },
  health_check:               { icon: ShieldCheck,      color: 'green'  },
  // Zaplanowane akcje
  scheduled_action:            { icon: Gear,             color: 'blue'   },
  // Sugestie
  suggestion_applied:         { icon: CheckCircle,      color: 'green'  },
  suggestion_dismissed:       { icon: Minus,            color: 'gray'   },
  // Kampanie
  campaign_published:         { icon: CheckCircle,      color: 'green'  },
  campaign_publish_failed:    { icon: Warning,          color: 'red'    },
  // System
  sync:                       { icon: ArrowsClockwise,  color: 'blue'   },
  report:                     { icon: ChartBar,         color: 'orange' },
  weekly_report:              { icon: ChartBar,         color: 'orange' },
  alert:                      { icon: Warning,          color: 'red'    },
};

const DEFAULT_CONFIG = { icon: Pulse, color: 'gray' };

// Business-oriented filter groups. Labele grup/pozycji tłumaczone w czasie renderu
// przez t('activity.filterGroups.<key>') oraz t('activity.filterItems.<value>').
const FILTER_OPTIONS = [
  { groupKey: 'optimizations', items: ['optimize', 'auto_apply', 'ad_group_update', 'campaign_settings_update', 'management_toggle'] },
  { groupKey: 'keywords', items: ['keyword_add', 'keyword_add_batch', 'keyword_pause', 'keyword_enable'] },
  { groupKey: 'suggestions', items: ['suggestion_applied', 'suggestion_dismissed'] },
  { groupKey: 'analysis', items: ['analyze', 'audit', 'health_alert'] },
  { groupKey: 'reports', items: ['report', 'weekly_report'] },
  { groupKey: 'schedule', items: ['scheduled_action'] },
  { groupKey: 'system', items: ['sync', 'campaign_published', 'alert'] },
];

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString('pl-PL', {
    hour: '2-digit', minute: '2-digit',
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleString('pl-PL', {
    hour: '2-digit', minute: '2-digit',
  });
}

function ResultSummary({ result, compact, currency }) {
  const { t } = useTranslation();
  if (!result || Object.keys(result).length === 0) return null;

  // Handle nested api_result from suggestion apply
  const r = result.api_result || result;
  // Waluta konta reklamowego: z wyniku, inaczej z wpisu logu (serializer), domyślnie „zł".
  const cur = currencySymbol(r.currency || result.currency || currency);
  const parts = [];

  if (result.campaigns_synced) parts.push(t('activity.result.campaignsSynced', { n: result.campaigns_synced }));
  if (result.campaigns_analyzed) parts.push(t('activity.result.campaignsAnalyzed', { n: result.campaigns_analyzed }));
  if (result.suggestions_created) parts.push(t('activity.result.suggestionsCreated', { n: result.suggestions_created }));
  if (r.total_cost) parts.push(t('activity.result.spend', { value: r.total_cost, cur }));
  if (r.overall_score) parts.push(t('activity.result.score', { value: r.overall_score }));
  else if (r.score) parts.push(t('activity.result.score', { value: r.score }));
  if (r.estimated_savings || r.savings) parts.push(t('activity.result.savings', { value: r.estimated_savings || r.savings, cur }));
  if (r.old_budget != null && r.new_budget != null) parts.push(t('activity.result.budget', { old: r.old_budget, new: r.new_budget, cur }));
  if (r.old_cpc != null && r.new_cpc != null) parts.push(t('activity.result.cpc', { old: r.old_cpc, new: r.new_cpc, cur }));
  if (r.old_status && r.new_status) {
    const sl = { paused: t('activity.result.statusPaused'), active: t('activity.result.statusActive') };
    parts.push(t('activity.result.status', { old: sl[r.old_status] || r.old_status, new: sl[r.new_status] || r.new_status }));
  }
  if (r.keyword) parts.push(`"${r.keyword}"`);
  if (r.type === 'auto_bid_increase') parts.push(t('activity.result.cpcBid', { old: r.old_bid, new: r.new_bid, cur }));
  if (r.type === 'negative_keyword_added') parts.push(t('activity.result.wasted', { value: r.wasted_spend?.toFixed(0), cur }));

  // Keyword lists from suggestion results
  if (r.keywords_added) parts.push(t('activity.result.keywordsAdded', { n: r.keywords_added }));
  if (r.keywords_paused) parts.push(t('activity.result.keywordsPaused', { n: r.keywords_paused }));
  if (r.negatives_added) parts.push(t('activity.result.negatives', { n: r.negatives_added }));
  if (r.headlines && Array.isArray(r.headlines)) {
    // Headlines can be strings or objects - only show count + strings
    const headlineStrings = r.headlines.filter(h => typeof h === 'string');
    parts.push(t('activity.result.headlines', { n: r.headlines.length }));
    if (headlineStrings.length > 0) {
      parts.push(headlineStrings.slice(0, 3).map(h => `"${h}"`).join(', '));
    }
  }
  if (r.ad_group) parts.push(t('activity.result.adGroup', { value: r.ad_group }));

  // Draft analysis: show keyword count and campaign name
  if (r.campaign_name && r.daily_budget) {
    parts.push(t('activity.result.dailyBudget', { value: r.daily_budget, cur }));
    if (r.keywords && Array.isArray(r.keywords)) parts.push(t('activity.result.keywordsCount', { n: r.keywords.length }));
  }

  // Show keyword list if available (strings only, skip objects)
  const keywords = r.keywords;
  const kwStrings = Array.isArray(keywords)
    ? keywords.map(k => typeof k === 'string' ? k : k?.text).filter(Boolean)
    : [];

  if (parts.length === 0 && kwStrings.length === 0) return null;

  if (compact) {
    const summary = parts.slice(0, 3).join(' · ');
    return summary || null;
  }

  return (
    <Box>
      {parts.length > 0 && <Text size="xs" c="dimmed" ff="monospace">{parts.join(' · ')}</Text>}
      {kwStrings.length > 0 && (
        <Text size="xs" c="dimmed" ff="monospace" mt={2}>
          {kwStrings.slice(0, 8).map(k => `"${k}"`).join(', ')}
          {kwStrings.length > 8 ? ` (+${kwStrings.length - 8})` : ''}
        </Text>
      )}
    </Box>
  );
}

export default function AgentActivity() {
  const { t } = useTranslation();
  const actionLabel = (action) => t(`activity.actions.${action}`, { defaultValue: action.replace(/_/g, ' ') });
  const { user } = useAuth();
  const isAgency = user?.organization?.is_agency;
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const filterType = searchParams.get('type') || null;
  const filterCampaign = searchParams.get('campaign') || null;
  const filterClient = searchParams.get('client') || null;

  const setPage = (p) => {
    const params = {};
    if (p > 1) params.page = p;
    if (filterType) params.type = filterType;
    if (filterCampaign) params.campaign = filterCampaign;
    if (filterClient) params.client = filterClient;
    setSearchParams(params, { replace: true });
  };

  const setFilter = (key, value) => {
    const params = {};
    // Reset page on filter change
    if (key === 'type') { if (value) params.type = value; }
    else { if (filterType) params.type = filterType; }
    if (key === 'campaign') { if (value) params.campaign = value; }
    else { if (filterCampaign) params.campaign = filterCampaign; }
    if (key === 'client') { if (value) params.client = value; }
    else { if (filterClient) params.client = filterClient; }
    setSearchParams(params, { replace: true });
  };

  const fetchLogs = useCallback(() => {
    setLoading(true);
    let url = `/accounts/logs/?page=${page}`;
    if (filterType) url += `&action=${filterType}`;
    if (filterCampaign) url += `&campaign_name=${filterCampaign}`;
    if (filterClient) url += `&client=${encodeURIComponent(filterClient)}`;
    api.get(url)
      .then(res => {
        const data = res.data;
        if (data.results) {
          setLogs(data.results);
          setTotalCount(data.count || 0);
          setTotalPages(Math.ceil((data.count || 0) / 20));
        } else {
          const items = Array.isArray(data) ? data : [];
          setLogs(items);
          setTotalCount(items.length);
          setTotalPages(1);
        }
      })
      .catch(() => { setLogs([]); setTotalCount(0); })
      .finally(() => setLoading(false));
  }, [page, filterType, filterCampaign, filterClient]);

  useEffect(() => {
    fetchLogs();
    api.get('/ai/status/').then(res => setAgentStatus(res.data)).catch(() => {});
  }, [fetchLogs]);

  // Build campaign filter options from first page (fetched separately)
  const [campaignOptions, setCampaignOptions] = useState([]);
  useEffect(() => {
    api.get('/accounts/logs/?page_size=200')
      .then(res => {
        const items = res.data.results || res.data || [];
        const names = [...new Set(items.map(l => l.campaign_name).filter(Boolean))];
        setCampaignOptions(names.map(n => ({ value: n, label: n })));
      })
      .catch(() => {});
  }, []);

  const isActive = agentStatus?.status === 'active' && agentStatus?.campaigns_monitored > 0;
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const hasActiveFilters = !!(filterType || filterCampaign || filterClient);
  const clearAllFilters = () => {
    setSearchParams({}, { replace: true });
    setShowFilterDrawer(false);
  };

  // Flatten filter options for Select
  const typeSelectData = FILTER_OPTIONS.map(g => ({
    group: t(`activity.filterGroups.${g.groupKey}`),
    items: g.items.map(v => ({ value: v, label: t(`activity.filterItems.${v}`) })),
  }));

  const activityClientNames = [...new Set(logs.map(l => l.agency_client_name).filter(Boolean))].sort();

  const exportFilters = [
    filterType ? `action=${filterType}` : '',
    filterCampaign ? `campaign_name=${encodeURIComponent(filterCampaign)}` : '',
    filterClient ? `client=${encodeURIComponent(filterClient)}` : '',
  ].filter(Boolean).join('&');

  return (
    <Box px="lg" py="md">
      <SEO title={t('activity.seoTitle')} path="/app/activity" noIndex />
      <StickyHeader>
        <Title order={3}>{t('activity.title')}</Title>
        <ExportMenu baseUrl="/accounts/logs/export/" filename="droid-aktywność" filters={exportFilters} />
      </StickyHeader>
      {/* Desktop filters */}
      <Group gap="xs" mb="md" className="hidden md:flex">
        <Select placeholder={t('activity.filters.typePlaceholder')} data={typeSelectData} value={filterType} onChange={(v) => setFilter('type', v)} clearable size="sm" style={{ width: 220 }} />
        {campaignOptions.length > 0 && (
          <Select placeholder={t('activity.filters.campaignPlaceholder')} data={campaignOptions} value={filterCampaign} onChange={(v) => setFilter('campaign', v)} clearable size="sm" style={{ width: 200 }} />
        )}
        {isAgency && activityClientNames.length > 0 && (
          <Select placeholder={t('activity.filters.clientPlaceholder')} data={activityClientNames.map(n => ({ value: n, label: n }))} value={filterClient} onChange={(v) => setFilter('client', v)} clearable size="sm" style={{ width: 180 }} />
        )}
      </Group>
      {/* Mobile filter */}
      <div className="md:hidden mb-3">
        <Button variant="default" size="sm" leftSection={<Funnel size={14} />} onClick={() => setShowFilterDrawer(true)}>
          {hasActiveFilters ? t('activity.filters.mobileButtonActive') : t('activity.filters.mobileButton')}
        </Button>
      </div>
      <Drawer opened={showFilterDrawer} onClose={() => setShowFilterDrawer(false)} title={t('activity.filters.drawerTitle')} position="bottom" size="auto" padding="md" className="md:hidden">
        <Stack gap="sm">
          <Select label={t('activity.filters.typeLabel')} placeholder={t('activity.filters.allNeuter')} data={typeSelectData} value={filterType} onChange={(v) => setFilter('type', v)} clearable size="sm" />
          {campaignOptions.length > 0 && (
            <Select label={t('activity.filters.campaignLabel')} placeholder={t('activity.filters.allNeuter')} data={campaignOptions} value={filterCampaign} onChange={(v) => setFilter('campaign', v)} clearable size="sm" />
          )}
          {isAgency && activityClientNames.length > 0 && (
            <Select label={t('activity.filters.clientLabel')} placeholder={t('activity.filters.allMasc')} data={activityClientNames.map(n => ({ value: n, label: n }))} value={filterClient} onChange={(v) => setFilter('client', v)} clearable size="sm" />
          )}
          <Group gap="xs" mt="xs">
            <Button size="sm" color="brand" onClick={() => setShowFilterDrawer(false)}>{t('activity.filters.apply')}</Button>
            {hasActiveFilters && <Button size="sm" variant="subtle" color="gray" onClick={clearAllFilters}>{t('activity.filters.clear')}</Button>}
          </Group>
        </Stack>
      </Drawer>

      {loading ? (
        <Flex align="center" justify="center" h={200}>
          <AnimatedLogo size={48} />
        </Flex>
      ) : (() => {
        const displayLogs = logs;
        return displayLogs.length > 0 ? (
        <>
          {/* Mobile cards */}
          <div className="md:hidden">
            <Stack gap="xs">
              {displayLogs.map((log, i) => {
                const config = ACTION_CONFIG[log.action] || DEFAULT_CONFIG;
                const Icon = config.icon;
                const label = actionLabel(log.action);
                return (
                  <Paper key={log.id || i} withBorder p="sm" radius="md">
                    <Group justify="space-between" mb={4}>
                      <Group gap={6}>
                        <ThemeIcon variant="light" color={config.color} size="sm" radius="sm">
                          <Icon size={12} weight="bold" />
                        </ThemeIcon>
                        <Text size="xs" fw={500} c={config.color === 'gray' ? 'dimmed' : `${config.color}.6`}>{label}</Text>
                      </Group>
                      <Text size="xs" c="dimmed" ff="monospace">{formatTime(log.created_at)}</Text>
                    </Group>
                    <Text size="sm" mb={4}>{log.description}</Text>
                    {log.campaign_name && <Text size="xs" c="dimmed">{log.campaign_name}</Text>}
                    <ResultSummary result={log.result} currency={log.currency} />
                  </Paper>
                );
              })}
            </Stack>
          </div>
          {/* Desktop table */}
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }} className="hidden md:block">
            <EdgeScroll>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover style={{ minWidth: 700 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 100 }}>{t('activity.table.date')}</Table.Th>
                  <Table.Th style={{ width: 140 }}>{t('activity.table.type')}</Table.Th>
                  <Table.Th>{t('activity.table.description')}</Table.Th>
                  <Table.Th style={{ width: 160 }}>{t('activity.table.campaign')}</Table.Th>
                  <Table.Th style={{ width: 180 }}>{t('activity.table.details')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {displayLogs.map((log, i) => {
                  const config = ACTION_CONFIG[log.action] || DEFAULT_CONFIG;
                  const Icon = config.icon;
                  const label = actionLabel(log.action);
                  return (
                    <Table.Tr key={log.id || i} onClick={() => setSelectedLog(log)} style={{ cursor: 'pointer' }}>
                      <Table.Td>
                        <Tooltip label={formatDate(log.created_at)} position="top">
                          <Text size="xs" c="dimmed" ff="monospace">{formatTime(log.created_at)}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <ThemeIcon variant="light" color={config.color} size="sm" radius="sm">
                            <Icon size={12} weight="bold" />
                          </ThemeIcon>
                          <Text size="xs" c={config.color === 'gray' ? 'dimmed' : `${config.color}.6`} fw={500} truncate>
                            {label}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>{log.description}</Text>
                      </Table.Td>
                      <Table.Td>
                        {log.campaign_name ? (
                          <Text size="xs" c="dimmed" truncate>{log.campaign_name}</Text>
                        ) : (
                          <Text size="xs" c="gray.4">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" lineClamp={1}><ResultSummary result={log.result} currency={log.currency} compact /></Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            </EdgeScroll>
          </Paper>
          {totalPages > 1 && (
            <Group justify="center" mt="lg">
              <Pagination
                value={page}
                onChange={setPage}
                total={totalPages}
                color="brand"
                size="sm"
              />
            </Group>
          )}
        </>
      ) : (
        <Paper p={48} withBorder style={{ textAlign: 'center' }}>
          <ThemeIcon variant="light" color="gray" size="xl" radius="md" mx="auto" mb="sm">
            <Pulse size={22} weight="duotone" />
          </ThemeIcon>
          <Text size="sm" fw={500} c="dark" mb={4}>
            {filterType || filterCampaign || filterClient ? t('activity.empty.filteredTitle') : t('activity.empty.title')}
          </Text>
          <Text size="xs" c="dimmed">
            {filterType || filterCampaign || filterClient
              ? t('activity.empty.filteredText')
              : t('activity.empty.text')}
          </Text>
        </Paper>
      );
      })()}

      {/* Detail modal */}
      {selectedLog && (() => {
        const config = ACTION_CONFIG[selectedLog.action] || DEFAULT_CONFIG;
        const Icon = config.icon;
        const label = actionLabel(selectedLog.action);
        const r = selectedLog.result || {};
        const api = r.api_result || {};
        const cur = currencySymbol(api.currency || r.currency || selectedLog.currency);
        return (
          <Modal opened onClose={() => setSelectedLog(null)} title={
            <Group gap="xs">
              <ThemeIcon variant="light" color={config.color} size="sm" radius="sm">
                <Icon size={12} weight="bold" />
              </ThemeIcon>
              <Text size="sm" fw={600}>{label}</Text>
            </Group>
          } size="lg">
            <Stack gap="sm">
              <Box>
                <Text size="xs" c="dimmed" mb={2}>{t('activity.detail.description')}</Text>
                <Text size="sm">{selectedLog.description}</Text>
              </Box>
              {selectedLog.campaign_name && (
                <Box>
                  <Text size="xs" c="dimmed" mb={2}>{t('activity.detail.campaign')}</Text>
                  <Text size="sm" fw={500}>{selectedLog.campaign_name}</Text>
                </Box>
              )}
              <Box>
                <Text size="xs" c="dimmed" mb={2}>{t('activity.detail.date')}</Text>
                <Text size="sm" ff="monospace">{formatDate(selectedLog.created_at)}</Text>
              </Box>

              {/* Structured details */}
              {(api.old_budget != null || api.old_cpc != null || api.keywords_added != null || api.keywords_paused != null || api.headlines || api.negatives_added != null || api.reason) && (
                <Box>
                  <Text size="xs" c="dimmed" mb={4}>{t('activity.detail.changesTitle')}</Text>
                  <Paper withBorder p="sm" radius="md" bg="gray.0">
                    <Stack gap={4}>
                      {api.old_budget != null && <Text size="sm">{t('activity.detail.budget')}: <strong>{api.old_budget} → {api.new_budget} {t('activity.detail.perDay', { cur })}</strong></Text>}
                      {api.old_cpc != null && <Text size="sm">{t('activity.detail.cpc')}: <strong>{api.old_cpc} → {api.new_cpc} {t('activity.detail.zl', { cur })}</strong> ({t('activity.detail.groupsCount', { n: api.ad_groups_updated || 0 })})</Text>}
                      {api.ad_groups && <Text size="xs" c="dimmed">{t('activity.detail.groups')}: {api.ad_groups.join(', ')}</Text>}
                      {api.keywords_added != null && <Text size="sm">{t('activity.detail.keywordsAdded')}: <strong>{api.keywords_added}</strong></Text>}
                      {api.keywords_paused != null && <Text size="sm">{t('activity.detail.keywordsPaused')}: <strong>{api.keywords_paused}</strong></Text>}
                      {api.negatives_added != null && <Text size="sm">{t('activity.detail.negativesAdded')}: <strong>{api.negatives_added}</strong></Text>}
                      {api.headlines && Array.isArray(api.headlines) && (
                        <Box>
                          <Text size="sm" mb={2}>{t('activity.detail.headlines')} ({api.headlines.length}):</Text>
                          {api.headlines.filter(h => typeof h === 'string').map((h, i) => (
                            <Text key={i} size="xs" c="dimmed" ff="monospace">"{h}"</Text>
                          ))}
                        </Box>
                      )}
                      {api.descriptions && Array.isArray(api.descriptions) && (
                        <Box>
                          <Text size="sm" mb={2}>{t('activity.detail.descriptions')} ({api.descriptions.length}):</Text>
                          {api.descriptions.filter(d => typeof d === 'string').map((d, i) => (
                            <Text key={i} size="xs" c="dimmed" ff="monospace">"{d}"</Text>
                          ))}
                        </Box>
                      )}
                      {api.message && <Text size="sm" c="dimmed">{api.message}</Text>}
                      {api.reason && <Text size="sm" c="red.6">{api.reason}</Text>}
                    </Stack>
                  </Paper>
                </Box>
              )}

              {/* Keywords list */}
              {(() => {
                const kws = api.keywords || r.keywords;
                const kwStrings = Array.isArray(kws) ? kws.map(k => typeof k === 'string' ? k : k?.text).filter(Boolean) : [];
                if (kwStrings.length === 0) return null;
                return (
                  <Box>
                    <Text size="xs" c="dimmed" mb={4}>{t('activity.detail.keywordsTitle')} ({kwStrings.length})</Text>
                    <Paper withBorder p="sm" radius="md" bg="gray.0">
                      <Text size="xs" ff="monospace" lh={1.8}>
                        {kwStrings.map(k => `"${k}"`).join(', ')}
                      </Text>
                    </Paper>
                  </Box>
                );
              })()}

              {/* Raw result for draft analyses */}
              {r.campaign_name && r.daily_budget && (
                <Box>
                  <Text size="xs" c="dimmed" mb={4}>{t('activity.detail.aiAnalysis')}</Text>
                  <Paper withBorder p="sm" radius="md" bg="gray.0">
                    <Stack gap={4}>
                      <Text size="sm">{t('activity.detail.name')}: <strong>{r.campaign_name}</strong></Text>
                      <Text size="sm">{t('activity.detail.budget')}: <strong>{r.daily_budget} {t('activity.detail.perDay', { cur })}</strong></Text>
                      {r.business_type && <Text size="sm">{t('activity.detail.type')}: {r.business_type}</Text>}
                      {r.target_audience && <Text size="sm">{t('activity.detail.targetAudience')}: {r.target_audience}</Text>}
                      {r.recommended_strategy && <Text size="sm">{t('activity.detail.strategy')}: <strong>{r.recommended_strategy}</strong></Text>}
                      {r.market_summary && <Text size="xs" c="dimmed" mt={4}>{r.market_summary}</Text>}
                    </Stack>
                  </Paper>
                </Box>
              )}
            </Stack>
          </Modal>
        );
      })()}
    </Box>
  );
}
